/**
 * Ticket ME1: Liste der Mentorings im Backend.
 *
 * Zugriff über den angemeldeten Client; die Datenbankrichtlinien lassen nur
 * Administratorinnen und Editorinnen lesen und pflegen, Löschen nur
 * Administratorinnen.
 *
 * Die Reihenfolge folgt dem gepflegten Feld, nicht dem Alphabet – dieselbe
 * Regel wie auf der öffentlichen Übersicht (`compareMentorings`).
 */

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, ImageIcon, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminEmptyState } from "@/components/admin/AdminEmptyState";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminSkeletonList } from "@/components/admin/AdminSkeletonList";
import { useAdminSession } from "@/hooks/use-admin-session";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";
import {
  MENTORING_STATUS_LABELS,
  compareMentorings,
  mentoringPath,
  type MentoringStatus,
} from "@/lib/mentorings";

export const Route = createFileRoute("/_admin/admin/mentorings/")({
  head: () => ({
    meta: [
      { title: "Mentorings – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MentoringsAdminPage,
});

type Row = {
  id: string;
  slug: string;
  title: string;
  sort_order: number;
  status: MentoringStatus;
  price_label: string | null;
  mentor_profile_id: string | null;
  preview_image_path: string | null;
};

const PREVIEW_URL_TTL_SECONDS = 60 * 60;

function MentoringsAdminPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const areaVisible = useFeatureFlag(FEATURE_FLAGS.mentoringsVisible);

  const mentorings = useQuery({
    queryKey: ["admin-mentorings"],
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await supabase
        .from("mentorings")
        .select(
          "id, slug, title, sort_order, status, price_label, mentor_profile_id, preview_image_path",
        )
        .order("sort_order", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as Row[];
    },
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (mentorings.data ?? [])
      .filter(
        (row) =>
          !term ||
          row.title.toLowerCase().includes(term) ||
          row.slug.toLowerCase().includes(term),
      )
      .map((row) => ({ ...row, sortOrder: row.sort_order }))
      .sort(compareMentorings);
  }, [mentorings.data, search]);

  const previewPaths = useMemo(
    () => [...new Set(rows.map((row) => row.preview_image_path).filter((path): path is string => Boolean(path)))],
    [rows],
  );
  const previewQueries = useQueries({
    queries: previewPaths.map((path) => ({
      queryKey: ["admin-mentoring-preview", path] as const,
      queryFn: async () => {
        const { data, error } = await supabase.storage
          .from("service-images")
          .createSignedUrl(path, PREVIEW_URL_TTL_SECONDS);
        if (error) throw new Error(error.message);
        return data.signedUrl;
      },
      staleTime: (PREVIEW_URL_TTL_SECONDS * 1000) / 2,
    })),
  });
  const previewUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    previewQueries.forEach((query, index) => {
      const path = previewPaths[index];
      if (path && query.data) urls[path] = query.data;
    });
    return urls;
  }, [previewPaths, previewQueries]);

  return (
    <div className="space-y-8">
      <AdminPageHeader
        title="Mentorings"
        description="Persönliche Begleitung – wird angefragt, nicht gekauft. Die Reihenfolge der Übersicht folgt dem Feld „Reihenfolge“."
        actions={
          <button
            type="button"
            onClick={() => navigate({ to: "/admin/mentorings/$id", params: { id: "neu" } })}
            className="inline-flex items-center gap-2 rounded-sm bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Mentoring anlegen
          </button>
        }
      />

      {/* Ticket ME1: zwei Regler. Der Status entscheidet je Mentoring, der
          Funktionsschalter über den ganzen Bereich. */}
      <AdminCard>
        <p className="font-body text-sm text-muted-foreground">
          {areaVisible.enabled
            ? "Der Bereich ist freigeschaltet: veröffentlichte Mentorings sind öffentlich erreichbar und stehen in der Sitemap."
            : "Der Bereich ist noch verborgen. Veröffentlichte Seiten sehen nur angemeldete Personen aus dem internen Bereich; im Menü und in der Sitemap erscheinen sie nicht. Freigeschaltet wird der Bereich unter Feature-Flags („mentorings_visible“)."}
        </p>
      </AdminCard>

      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Suchen nach Titel oder Kürzel"
        className="w-full max-w-sm rounded-sm border border-border bg-background px-3 py-2 text-sm"
      />

      {mentorings.isLoading && <AdminSkeletonList />}
      {mentorings.isError && (
        <AdminErrorNotice message={(mentorings.error as Error)?.message} />
      )}

      {mentorings.data && rows.length === 0 && (
        <AdminEmptyState title="Noch kein Mentoring angelegt." />
      )}

      {rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li
              key={row.id}
              className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-4 rounded-sm border border-foreground/15 p-4 sm:grid-cols-[6rem_minmax(0,1fr)_auto] sm:p-5"
            >
              <div className="aspect-square w-20 overflow-hidden rounded-sm bg-muted sm:w-24">
                {row.preview_image_path && previewUrls[row.preview_image_path] ? (
                  <img
                    src={previewUrls[row.preview_image_path]}
                    alt={`Vorschaubild zu ${row.title || row.slug}`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center text-muted-foreground"
                    aria-label="Kein Vorschaubild vorhanden"
                  >
                    <ImageIcon className="h-6 w-6" aria-hidden />
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <Link
                  to="/admin/mentorings/$id"
                  params={{ id: row.id }}
                  className="font-display text-base font-extrabold text-foreground hover:underline"
                >
                  {row.title || row.slug}
                </Link>
                <p className="mt-1 font-body text-sm text-muted-foreground">
                  Reihenfolge {row.sort_order} · {MENTORING_STATUS_LABELS[row.status]} ·{" "}
                  {row.price_label?.trim() || "kein Preis"} ·{" "}
                  {row.mentor_profile_id ? "Betreuung zugeordnet" : "keine Betreuung"}
                </p>
              </div>
              <a
                href={mentoringPath(row.slug)}
                target="_blank"
                rel="noopener"
                className="col-start-2 inline-flex items-center gap-2 font-body text-sm text-primary hover:underline sm:col-start-auto"
              >
                Seite ansehen
                <ExternalLink className="h-4 w-4" aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


